// Pieces the Case and person screens share: section headings, a thing shown by
// name that opens where its part registered, and a day picker (ui/date-popover).

import { Link } from "@tanstack/react-router";
import type { CaseTarget } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import type { ReactNode, RefObject } from "react";
import { useMeApp } from "../app/context";
import {
  defineDeviceSetting,
  useDeviceSetting,
} from "../device/device-setting";
import { useTimeZone } from "../me/profile";
import { DatePopover } from "../ui/date-popover";
import { Caret, Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { useCasesScreen } from "./screen-state";

/** A heading between sections: small, spaced, faint, never bold. */
export const SECTION = "mb-2 text-[11px] tracking-[0.04em] text-faint";

/**
 * The body column of a detail: the column of a document, centered, with a
 * fifth of the screen's height of room at the end (the last step opened to
 * write never sits on the bottom edge). The box is 760px with 32px sides (a
 * 696px reading width); narrower, the sides give way first, down to 16px.
 */
export function DetailBody({
  children,
  scrollRef,
}: {
  children: ReactNode;
  scrollRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={scrollRef}
      className="panel-scroll min-h-0 flex-1 select-text overflow-y-auto"
    >
      <div className="mx-auto w-full max-w-[760px] pt-7 pb-[20vh] [padding-inline:clamp(16px,calc((100%-696px)/2),32px)]">
        {children}
      </div>
    </div>
  );
}

/** Sections closed on this device (per section, not per Case; the old web's key). */
const closedSections = defineDeviceSetting<readonly string[]>({
  key: "trameria-case-sections-closed",
  parse: (raw) => (raw ?? "").split(",").filter(Boolean),
  serialize: (v) => v.join(","),
});

/** Whether a section (Related, a person's history) is open, kept on this device. */
export function useSectionOpen(name: string): [boolean, () => void] {
  const [closed, setClosed] = useDeviceSetting(closedSections);
  const open = !closed.includes(name);
  return [
    open,
    () =>
      setClosed(open ? [...closed, name] : closed.filter((x) => x !== name)),
  ];
}

/** A section heading that opens and closes what follows (the caret turns, never swaps). */
export function SectionToggle({
  label,
  open,
  onToggle,
  controls,
  className = "mt-7",
}: {
  label: string;
  open: boolean;
  onToggle(): void;
  controls: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className={`${SECTION} ${className} -ml-[6px] flex cursor-pointer items-center gap-[6px] rounded-[6px] px-[6px] py-[2px] hover:bg-hover`}
    >
      <span className="tabular-nums">{label}</span>
      <Caret open={open} />
    </button>
  );
}

/**
 * How a Waiting names its target: as the target's part says for a waiting
 * (`FavoriteKind.waitName`: an item of the owner's turn says where to answer,
 * an Action says its Case too), else by the target's own name.
 */
export function useWaitName(): (target: CaseTarget) => string {
  const { favoriteKinds } = useMeApp();
  return (target) =>
    favoriteKinds[target.kind]?.waitName?.(target) ?? target.label;
}

/**
 * Something a Case points at (a wait target, a related row), by its current
 * name. It opens where its part registered (with what the server answered
 * beyond the id: the chat an item of the owner's turn sits in); one the app
 * cannot open, or that is gone, is struck through and says why.
 */
export function TargetName({
  target,
  className = "",
  showIcon = true,
  asWait = false,
}: {
  target: CaseTarget;
  className?: string;
  /** False when the row draws the kind's icon itself. */
  showIcon?: boolean;
  /** Named as a Waiting names its target (`useWaitName`). */
  asWait?: boolean;
}) {
  const { favoriteKinds } = useMeApp();
  const waitName = useWaitName();
  const screen = useCasesScreen();
  const view = favoriteKinds[target.kind];
  // In the cloud (a Hub's desktop): named, or out of reach, but not missing.
  if (target.unreachable || (target.visible && !view)) {
    const away = !!target.unreachable;
    const why = away ? m.refs_unreachable() : m.refs_in_cloud();
    return (
      <Tooltip text={why}>
        <button
          type="button"
          aria-disabled="true"
          data-ref-elsewhere=""
          data-ref-unreachable={away ? "" : undefined}
          aria-label={
            target.label
              ? m.refs_missing_label({ label: target.label, why })
              : why
          }
          className={`inline-flex min-w-0 cursor-default items-center gap-1 truncate text-left text-muted ${className}`}
        >
          {away && <Icon kind="unreachable" size={15} />}
          {target.label && <span className="truncate">{target.label}</span>}
        </button>
      </Tooltip>
    );
  }
  if (!target.visible || !view) {
    const why =
      !target.known || !view ? m.refs_cannot_open_here() : m.refs_not_found();
    return (
      <Tooltip text={why}>
        {/* A button that does nothing: it can take the focus, so the reason can be read from the keyboard. */}
        <button
          type="button"
          aria-disabled="true"
          data-ref-missing=""
          aria-label={m.refs_missing_label({ label: target.label, why })}
          className={`min-w-0 cursor-default truncate text-left text-faint line-through ${className}`}
        >
          {target.label || target.id}
        </button>
      </Tooltip>
    );
  }
  const KindIcon = view.icon;
  // The Cases part's own kinds open as the list's rows do: another Case from
  // its top, an Action's Case with the Action open and brought into view.
  const caseOf =
    target.kind === "case"
      ? target.id
      : target.kind === "action" && typeof target.path?.caseId === "string"
        ? target.path.caseId
        : null;
  return (
    <Link
      to={view.to(target.id, target.path)}
      onClick={
        caseOf === null
          ? undefined
          : () =>
              screen.openStep(
                caseOf,
                target.kind === "action" ? target.id : null,
                { reveal: true },
              )
      }
      className={`inline-flex min-w-0 max-w-full items-center gap-[4px] align-bottom hover:text-fg hover:underline ${className}`}
    >
      {showIcon && (
        // A waiting's target has the old web's 14px mark.
        <KindIcon
          className={`${asWait ? "h-[14px] w-[14px]" : "h-[13px] w-[13px]"} flex-none`}
        />
      )}
      <span className="min-w-0 truncate">
        {asWait ? waitName(target) : target.label}
      </span>
    </Link>
  );
}

/**
 * A day picked in a panel (due dates, the day to wait until): the calendar, or
 * the phone's own picker. Picking or clearing applies at once.
 */
export function DayPopover({
  anchor,
  open,
  onClose,
  label,
  value,
  onChange,
  clearLabel,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose(): void;
  label: string;
  /** YYYY-MM-DD or "". */
  value: string;
  onChange(date: string | null): void;
  clearLabel: string;
}) {
  const timeZone = useTimeZone();
  return (
    <DatePopover
      anchor={anchor}
      open={open}
      onClose={onClose}
      label={label}
      align="end"
      value={value}
      clearLabel={clearLabel}
      timeZone={timeZone}
      onChange={(date) => onChange(date || null)}
    />
  );
}
